import { E as EmblaCarousel } from './embla-carousel.esm-DpJzJcqm.js';
import { f as fetchHTML } from './fetchHTML-9PhJW9Dj.js';
import { g as getRecentlyViewed } from './recentlyViewed-CW8LAALU.js';
import { e as emblaNavigation } from './navigation-DGcbxkcb.js';

class RecentlyViewed extends HTMLElement {
  #content = this.querySelector('.recently-viewed');
  #viewport = this.querySelector('.embla__viewport');
  #container = this.querySelector('.embla__container');
  #prevButton = this.querySelector('.slider-button-prev');
  #nextButton = this.querySelector('.slider-button-next');
  /** @type {import('embla-carousel').EmblaCarouselType} */
  #embla;

  // The section sits below the fold: only fetch the cards once it is scrolled towards
  #observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) return;

      this.#observer.disconnect();
      this.#render();
    },
    { rootMargin: '400px' }
  );

  connectedCallback() {
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    this.#embla?.destroy();
  }

  /**
   * Fetch a single product card and wrap it in a slide
   * @param {string} handle - The handle of the product to fetch
   * @returns {Promise<HTMLElement|null>} - The slide, or null when the fetch failed
   */
  async #fetchSlide(handle) {
    const root = Shopify.routes?.root || '/';

    try {
      const html = await fetchHTML(`${root}products/${handle}?section_id=fetch-product-card`);
      const card = html
        .getElementById('shopify-section-fetch-product-card')
        ?.querySelector('product-card');

      if (!card) return null;

      const slide = document.createElement('div');
      slide.className = this.#container.dataset.slideClass;
      slide.append(card);

      return slide;
    } catch (error) {
      console.error('Failed to fetch recently viewed product', error);
      return null;
    }
  }

  /**
   * Resolve the product a slide links to, so the same product can be recognised across handles
   * @param {HTMLElement} slide - The slide wrapping a product card
   * @returns {string|null} - The product path, or null when the card holds no product link
   */
  #productPath(slide) {
    const link = slide.querySelector('a[href*="/products/"]');

    if (!link) return null;

    return new URL(link.href, window.location.origin).pathname;
  }

  /**
   * Populate the slider from localStorage, excluding the product currently being viewed
   */
  async #render() {
    if (!this.#content || !this.#container) return;

    const handles = getRecentlyViewed().filter((handle) => handle !== this.dataset.productHandle);

    if (!handles.length) return;

    const slides = await Promise.all(handles.map((handle) => this.#fetchSlide(handle)));

    // Second pass on top of the deduplicated handles: one product can be stored under more than
    // one handle (localized handles share a single localStorage list), and those only become
    // recognisable as the same product once the cards are fetched. Seeded with the product page
    // itself so it stays excluded when it was stored under a different handle.
    const seen = new Set(this.dataset.productHandle ? [window.location.pathname] : []);

    const rendered = slides.filter((slide) => {
      if (!slide) return false;

      const path = this.#productPath(slide);

      if (!path) return true;
      if (seen.has(path)) return false;

      seen.add(path);
      return true;
    });

    if (!rendered.length) return;

    this.#container.replaceChildren(...rendered);
    this.#content.hidden = false;
    this.#initCarousel();
  }

  /**
   * Initialize the carousel, after the slides have been added to the DOM
   */
  #initCarousel() {
    if (!this.#viewport) return;

    this.#embla = EmblaCarousel(this.#viewport, {
      align: 'start',
      skipSnaps: true,
    });

    if (!this.#prevButton || !this.#nextButton) return;
    emblaNavigation(this.#embla, this.#prevButton, this.#nextButton);
  }
}

customElements.define('recently-viewed', RecentlyViewed);
